/** @jsx jsx */
import { ReactNode } from 'react';
import { css, jsx } from '@emotion/react';

import { Animation, Shadows } from '../../../theme';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { ServiceIconSize } from '../ServiceIcon';
import { ServiceIconConfig } from '../ServiceIcon/constants';

type ServiceIconShellProps = {
  /** The size of ServiceIconShell component to render. */
  size: ServiceIconSize;
  /** Applies a shadow to the ServiceIconShell. */
  hasShadow?: boolean;
  /**
   * Controls the presence/absence of the border.
   * It is recommended that a borderless ServiceIcon be contained in a larger, bordered container.
   */
  hasBorder?: boolean;
  /** The children to render inside of the `ServiceIconShell` component. */
  children?: ReactNode;
  /** Changes the background color to white (#fff). */
  hasWhiteBackground?: boolean;
};

const Styles = labelStyles('ServiceIconShell', {
  root: ({
    hasBorder,
    hasShadow,
    hasWhiteBackground,
    size,
  }: ServiceIconShellProps) => {
    const border = hasBorder ? `var(--zds-border-weak)` : `none`;
    const background = hasWhiteBackground
      ? '#fff'
      : 'var(--zds-background-weaker)';

    return [
      css`
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: ${background};
        border: ${border};
        border-radius: ${ServiceIconConfig[size].borderRadius};
      `,
      withModifier(
        `size`,
        css`
          height: ${ServiceIconConfig[size].width};
          width: ${ServiceIconConfig[size].width};
        `
      ),
      hasShadow &&
        withModifier(
          'shadowed',
          css`
            box-shadow: ${Shadows.elevation5};
            transition-duration: ${Animation.transitionDuration};
            transition-property: box-shadow;
            transition-timing-function: ${Animation.transitionTimingFunction};
          `
        ),
    ];
  },
});

/**
 * Renders the shell around an icon or number, representing one or more services.
 *
 * **This component isn't used on its own, but is exposed so that other components that are imperative to keep stylistically synced can use it.**
 */
export const ServiceIconShell = ({
  children,
  hasBorder = true,
  hasShadow = false,
  hasWhiteBackground = false,
  size,
}: ServiceIconShellProps) => {
  return (
    <span
      css={Styles.root({
        hasBorder,
        hasShadow,
        hasWhiteBackground,
        size,
      })}
      data-zds
      data-testid="ServiceIconShell"
    >
      {children}
    </span>
  );
};
